// src/Components/DeviceAddModal.jsx
import React, { useRef, useEffect, useState, useMemo } from 'react';
import '../css/DeviceAddModal.css';
import api from '../utils/api'; // ✅ default import로 교체
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

export default function DeviceAddModal({
  onClose,
  onSubmit,
  newDevice,
  setNewDevice,
  locationName,
  floorName,
  floorplanUrl
}) {
  const imgRef = useRef(null);
  const [existingDevices, setExistingDevices] = useState([]);

  // 🔸 로그인 선행검사는 제거
  //  - 만약 비로그인/만료면 요청 시 401 → axios 인터셉터가 refresh 시도/실패 시 /login 이동
  // useEffect(() => { ... }, []);

  // 해당 location+floor의 기존 디바이스 로드
  useEffect(() => {
    if (!locationName || !floorName) return;
    let ignore = false;
    (async () => {
      try {
        const res = await api.get('/devices', {
          params: { location: locationName, floor: floorName },
        });
        if (!ignore) setExistingDevices(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('기존 장비 불러오기 실패', e);
      }
    })();
    return () => { ignore = true; };
  }, [locationName, floorName]);



  
  // 마커 찍기
  const handleImageClick = (e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const relativeX = offsetX / rect.width;
    const relativeY = offsetY / rect.height;

    setNewDevice((prev) => ({
      ...prev,
      location: locationName,
      floor: floorName,
      x: Number(relativeX.toFixed(4)),
      y: Number(relativeY.toFixed(4)),
      rotation: prev.rotation || 0,
    }));
  };

  // === 설치 방향 선택 핸들러 ===
  const handleDirectionChange = (dir) => {
    // dir: 'up' | 'down' | 'left' | 'right'
    // 필요하면 rotation도 같이 맞춰 줌
    const rotationMap = {
      up: 0,
      right: 90,
      down: 180,
      left: 270,
    };

    setNewDevice((prev) => ({
      ...prev,
      installedDirection: dir,        // ✅ 서버로 보낼 방향
      rotation: rotationMap[dir] ?? prev.rotation ?? 0,
      // 만약 서버에서 "InstalledDirection" 대문자 필드를 그대로 요구하면
      // 아래 한 줄도 같이 써줘도 됨:
      // InstalledDirection: dir,
    }));
  };

  const currentDirection = newDevice.installedDirection || 'up';

  const planUrl = useMemo(() => {
    if (!floorplanUrl) return '';
    return floorplanUrl.startsWith('/')
      ? `${window.location.origin}${floorplanUrl}`
      : floorplanUrl;
  }, [floorplanUrl]);

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>➕ 장비 등록</h2>

        <div className="meta-row">
          <div className="pill">{locationName}</div>
          <div className="pill">{floorName}</div>
        </div>

        <input
          type="text"
          placeholder="디바이스 ID"
          value={newDevice.id ?? ''}
          onChange={(e) => setNewDevice({ ...newDevice, id: e.target.value })}
        />
        <input
          type="text"
          placeholder="비고 (메모)"
          value={newDevice.memo ?? ''}
          onChange={(e) => setNewDevice({ ...newDevice, memo: e.target.value })}
        />

        <div className="floorplan-container-add">
          <TransformWrapper initialScale={1} centerOnInit={false} centerZoomedOut={false} limitToBounds>
            <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
              <div className="zoom-image-wrapper" onClick={handleImageClick}>
                <img ref={imgRef} src={planUrl} alt="floorplan" className="floorplan-image" />
                {/* 기존 장비 */}
                {existingDevices.map((d) =>
                  d.x != null && d.y != null ? (
                    <div
                      key={d.id}
                      className="device-marker"
                      style={{ left: `${d.x * 100}%`, top: `${d.y * 100}%` }}
                      title={d.id}
                    >
                      <img src="/images/LES_normal_ICON.png" alt="마커" className="device-marker-img" />
                      <span className="device-label">{d.id}</span>
                    </div>
                  ) : null
                )}
                {/* 신규 마커 */}
             {newDevice.x != null && newDevice.y != null && (
  <div
    className="new-device-marker"
    style={{
      position: 'absolute',
      left: `${newDevice.x * 100}%`,
      top: `${newDevice.y * 100}%`,
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 4,
      pointerEvents: 'auto', // 필요하면 클릭 잘 되게
    }}
  >
    {/* 실제 마커 이미지 (회전 적용) */}
    <img
      src="/images/LES_normal_ICON.png"
      alt="new marker"
      className="device-marker-img"
      style={{
        transform: `rotate(${newDevice.rotation || 0}deg)`,
      }}
    />

    {/* 마커 아래 회전/방향 버튼 */}
    <div className="marker-rotate-buttons">
      {['up', 'right', 'down', 'left'].map((dir) => (
        <button
          key={dir}
          type="button"
          className={
            'marker-rotate-btn' +
            (currentDirection === dir ? ' marker-rotate-btn-active' : '')
          }
          onClick={(e) => {
            e.stopPropagation(); // 바닥 클릭 이벤트(마커 찍기)랑 안 섞이게
            handleDirectionChange(dir);
          }}
        >
          {dir === 'up' && '↑'}
          {dir === 'right' && '→'}
          {dir === 'down' && '↓'}
          {dir === 'left' && '←'}
        </button>
      ))}
    </div>
  </div>
)}

              </div>
            </TransformComponent>
          </TransformWrapper>
        </div>



        <div className="modal-buttons">
          <button
            onClick={async () => {
              // 서버에 필요한 필드 shape: DeviceDTO { id, location, floor, memo, x, y, ... }
              await onSubmit?.();
            }}
          >
            등록
          </button>
          <button onClick={onClose}>취소</button>
        </div>
      </div>
    </div>
  );
}
